import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import './AnalysisTab.css';
import searchIcon from '../../Images/search-icon.svg';
import filterIcon from '../../Images/settings-icon.svg'; // Using settings icon as filter
import downIcon from '../../Images/chevron-down.svg';
import calendarIcon from '../../Images/calendar.svg';
import downloadIcon from '../../Images/download-icon.svg';
import { getAssetTypes, AssetTypeResponse, queryTelemetryData, TelemetryQueryRequest, downloadTelemetryData, TelemetryDownloadRequest, getAssetDetails, AssetDetailsResponse, AssetAttribute } from '../../Services/serviceURL';
import { createAmChartsRoot } from '../../utils/amChartsConfig';
import DateRangePicker from '../../Components/common/date-range-picker/DateRangePicker';
import { SidebarOption } from '../../Components/common/date-range-picker/types';
// Add this import at the top
import colorPickerIcon from '../../Images/color-picker.svg';
import scaleIcon from '../../Images/scale.png';
import Loader from '../../Components/common/loader/loader';
import IndividualChart from './IndividualChart';

// Extended API Response interfaces to match the expected API structure
interface ExtendedAssetTypeParameter {
  tag: string;
  sensorName: string;
  isUserConfigurable?: boolean;
  units?: {
    code: string;
    description: string;
  }[];
  channelId: string;
  position?: string;
}

// Sensor data interface for chart
interface SensorData {
  id: string; // Unique identifier for React keys
  channelId: string;
  sensorName: string;
  unit: string;
  color: string;
  dataPoints: { date: string; value: number }[];
  minValue?: number;
  maxValue?: number;
  assetType?: string; // Add assetType to track which asset type this sensor belongs to
  // Threshold data
  alarmHData?: { timestamp: string; value: number }[];
  alarmHHData?: { timestamp: string; value: number }[];
  alarmLData?: { timestamp: string; value: number }[];
  alarmLLData?: { timestamp: string; value: number }[];
}

interface AnalysisTabProps {
  assetId?: string;
  assetType?: string; // Add assetType prop
  assetParameters?: AssetAttribute[]; // Asset attributes from getAssetDetails API
  triggerInitialLoad?: boolean;
}

const AnalysisTab: React.FC<AnalysisTabProps> = ({ assetId: propAssetId, assetType, assetParameters,triggerInitialLoad }) => {
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);
  const [pendingSensors, setPendingSensors] = useState<string[]>([]); // Temporary selection before Apply
  const [showFilterPopup, setShowFilterPopup] = useState<boolean>(false);
  const [searchInputVal, setSearchInputVal] = useState<string>('');
  const [sensors, setSensors] = useState<SensorData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [chartDataLoading, setChartDataLoading] = useState<boolean>(false);
  const [hasNoData, setHasNoData] = useState<boolean>(false);
  const [initialDataLoadComplete, setInitialDataLoadComplete] = useState<boolean>(false);
  const [sensorsInitialized, setSensorsInitialized] = useState<boolean>(false);
  const [chartCreated, setChartCreated] = useState<boolean>(false);
  const [lastChartDataKey, setLastChartDataKey] = useState<string>('');
  const [isTabVisible, setIsTabVisible] = useState<boolean>(true);
  const [hasValidChartData, setHasValidChartData] = useState<boolean>(false); // Track if we have valid data for chart
  const [chartDataSnapshot, setChartDataSnapshot] = useState<SensorData[]>([]); // Snapshot of chart data for persistence

  // Calendar modal and datetime picker states
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('Last 1 hour');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);
  // Add these with your existing useState declarations
  const [showColorPickerFor, setShowColorPickerFor] = useState<string | null>(null);
  const [colorPickerPosition, setColorPickerPosition] = useState<{ x: number; y: number } | null>(null);
  const chartInstanceRef = useRef<am5xy.XYChart | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const calendarModalRef = useRef<HTMLDivElement>(null);
  const telemetryFetchingRef = useRef<boolean>(false); // Prevent multiple simultaneous calls
  const telemetryTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounce mechanism
  const chartCreationRef = useRef<boolean>(false); // Prevent multiple simultaneous chart creations
  const chartCreationTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounce chart creation

  // Add this with your existing useRef declarations
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [showDatePicker,setShowDatePicker] = useState(false);
  const [selectedSidebarOption, setSelectedSidebarOption] = useState<string | null>(null);
  const [browserTimezone, setBrowserTimezone] = useState<string>(''); // Browser detected timezone

  // Custom Scaling Modal States
  const [showCustomScalingModal, setShowCustomScalingModal] = useState<boolean>(false);
  const [unitScalingValues, setUnitScalingValues] = useState<{
    [key: string]: { min: string; max: string; displayName?: string; actualUnit?: string }
  }>({});
  
  // Persistent state for manually entered scaling values (retains values between modal opens)
  const [manuallyEnteredScalingValues, setManuallyEnteredScalingValues] = useState<{
    [unit: string]: { min: string; max: string }
  }>({});
  
  // Track which units were actually modified by user during current modal session
  const [modifiedUnits, setModifiedUnits] = useState<Set<string>>(new Set());
  
  // Custom Scaling - now supports multiple units
  const [customScaling, setCustomScaling] = useState<{
    [unit: string]: { min: number; max: number }
  } | null>(null);
  
  // Chart mode state - Individual or Group
  const [chartMode, setChartMode] = useState<'individual' | 'group'>('individual');
  // Store individual mode sensor selection to restore when switching back
  const [individualModeSensors, setIndividualModeSensors] = useState<string[]>([]);
  // Store group mode sensor selection to restore when switching back
  const [groupModeSensors, setGroupModeSensors] = useState<string[]>([]);
  
  // Store uomRanges from API response
  const [uomRanges, setUomRanges] = useState<{
    uom: string;
    minValue: number;
    maxValue: number;
  }[]>([]);

  // Export functionality state
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>(''); // Store asset name for file naming


  // Sidebar options for DateRangePicker (maps to our time range options)
  const sidebarOptions: SidebarOption[] = [
    'Last 1 hour',
    'Last 2 hours',
    'Last 4 hours',
    'Last 8 hours',
    'Last 24 hours',
    'Last 7 days'
  ];
    // Helper function to map selectedTimeRange to SidebarOption
  const getSelectedSidebarOption = (): SidebarOption | null => {
    // Only return a sidebar option if it's one of the predefined options, not custom
    if (selectedTimeRange === 'Custom date range') {
      return null;
    }
    // Check if the selectedTimeRange matches any sidebar option
    return sidebarOptions.find(option => option === selectedTimeRange) || null;
  };

  // Detect browser timezone on component mount
  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setBrowserTimezone(timezone);

  }, []);

  // Use prop assetId
  const assetId = propAssetId;


  // Initialize applied dates based on default time range on component mount
  useEffect(() => {
    if (!appliedStartDate && !appliedEndDate) {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000);
      setAppliedStartDate(oneHourAgo);
      setAppliedEndDate(now);
    }
  }, []);

  // Cleanup effect to clear timeouts and prevent memory leaks
  useEffect(() => {
    return () => {
      if (telemetryTimeoutRef.current) {
        clearTimeout(telemetryTimeoutRef.current);
      }
      if (chartCreationTimeoutRef.current) {
        clearTimeout(chartCreationTimeoutRef.current);
      }
      // Only dispose chart on unmount, not on state changes
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
      }
      // Reset flags
      telemetryFetchingRef.current = false;
      chartCreationRef.current = false;
    };
  }, []);

  // Color palette for sensors
  const colorPalette = [
    '#00FFFF',
    '#00789e',
    '#CD7F32',
    '#A52A2A',
    '#808000',
    '#AAFF00',
    '#097969',
    '#228B22',
    '#FBCEB1',
    '#FFAC1C',
    '#DE3163',
    '#F88379',
    '#FF00FF',
    '#800080',
    '#5D3FD3',
    '#F5DEB3',
    '#FF0000',
    '#FFEA00',
    '#000080'
  ];
  // Add this after the defaultColorPalette definition (around line 110)
  const colorOptions = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF',
    '#00FFFF', '#FFA500', '#800080', '#008000', '#000080',
    '#A52A2A', '#808000', '#FFC0CB', '#FFD700', '#E6E6FA',
    '#00FF7F', '#D2691E', '#DC143C', '#00CED1', '#8A2BE2'
  ];
  // Function to validate GUID format
  const isValidGuid = (guid: string): boolean => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  };

  // API function to fetch sensors for a specific asset
  const fetchAssetSensors = async (assetId: string): Promise<SensorData[]> => {
    try {
      setLoading(true);

      const assetDetailsResponse = await getAssetDetails(assetId);

      if (assetDetailsResponse && assetDetailsResponse.attributes && assetDetailsResponse.attributes.length > 0) {
        // Store asset name for export filename
        if (assetDetailsResponse.assetName) {
          setAssetName(assetDetailsResponse.assetName);
        }

        // Filter attributes that have channelId to get only sensor parameters
        const sensorAttributes = assetDetailsResponse.attributes.filter(attr => attr.channelId);
        if (sensorAttributes.length > 0) {
          return createSensorsFromAssetDetails(sensorAttributes);
        } else {
          return await fetchAssetTypes();
        }
      } else {
        return await fetchAssetTypes();
      }
    } catch (error) {
      // Fallback to asset types if asset details fail
      return await fetchAssetTypes();
    } finally {
      setLoading(false);
    }
  };
  // Add these functions after your other handler functions

  const handleSensorColorChange = (sensorId: string, color: string, closePicker: boolean = true) => {
    setSensors(prevSensors =>
      prevSensors.map(sensor =>
        sensor.id === sensorId ? { ...sensor, color } : sensor
      )
    );

    // Reset chart so it recreates with new colors
    setChartCreated(false);
    setLastChartDataKey('');
    // Only close color picker if explicitly requested
    if (closePicker) {
      setShowColorPickerFor(null);
      setColorPickerPosition(null);
    }
  };

  const openColorPicker = (sensorId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    setColorPickerPosition({
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5
    });

    setShowColorPickerFor(sensorId);
  };

  // Function to get min/max values for a specific unit from API data only
  const getMinMaxForUnit = (unit: string) => {
    const actualUnit = unit === 'Other' ? '' : unit;
    
    // Get values from API uomRanges only
    const uomRange = uomRanges.find(range => range.uom === actualUnit);
    if (uomRange) {
      return {
        min: Math.floor(uomRange.minValue), // Round down to whole number
        max: Math.ceil(uomRange.maxValue)   // Round up to whole number
      };
    }
    
    // Return empty values if API data not available
    return { min: '', max: '' };
  };

  // Export functionality handler
  const handleExportData = async () => {
    // Prevent duplicate calls
    if (isExporting) {
      return;
    }
    
    
    if (!assetId || selectedSensors.length === 0) {
      alert('Please select sensors to export data');
      return;
    }

    setIsExporting(true);

    try {
      const Timerange = getRelativeTimeValue();

      // Use the same payload structure as telemetryRequest in fetchTelemetryData
      const downloadRequest: TelemetryDownloadRequest = {
        AssetId: assetId,
        ChannelIds: selectedSensors,
        timeRange: Timerange,
      };

      // Apply the same conditional logic as fetchTelemetryData
      if (selectedTimeRange === 'Custom date range') {
        const { startTime, endTime } = getDateRange();
        downloadRequest.StartTime = startTime;
        downloadRequest.EndTime = endTime;
      } else {
        downloadRequest.timeRange = Timerange;
      }


      const response = await downloadTelemetryData(downloadRequest);
      
      // Create blob and download file
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with asset name and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileNameBase = assetName || 'telemetry-data'; // Use asset name, fallback to assetId, then generic name
      // Clean filename to remove invalid characters
      const cleanFileName = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-');
      link.download = `${cleanFileName}-${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      
    } catch (error) {
      console.error('Error exporting telemetry data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Custom Scaling Modal Handlers
  const handleCustomScalingOpen = () => {
    // Get selected sensors
    const selectedSensorsList = sensors.filter(sensor => selectedSensors.includes(sensor.channelId));
    
    // Pre-populate min/max values for all unique units and sensors without units
    const initialValues: { [key: string]: { min: string; max: string; displayName?: string; actualUnit?: string } } = {};
    
    // Group sensors by unit, but handle sensors without units individually
    const processedKeys = new Set<string>();
    
    selectedSensorsList.forEach(sensor => {
      const unit = sensor.unit?.trim() || '';
      let displayKey: string;
      let displayName: string;
      let actualUnit: string;
      
      if (unit === '') {
        // For sensors without units, use "Others" as display name but sensor name as key
        displayKey = sensor.sensorName;
        displayName = "Others";
        actualUnit = ''; // Empty unit for API lookup
      } else {
        // For sensors with units, group by unit
        displayKey = unit;
        displayName = unit;
        actualUnit = unit;
      }
      
      // Skip if we've already processed this key
      if (processedKeys.has(displayKey)) {
        return;
      }
      processedKeys.add(displayKey);
      
      // Check if we have manually entered values for this key, otherwise use API values
      if (manuallyEnteredScalingValues[displayKey]) {
        // Use previously entered manual values
        initialValues[displayKey] = {
          min: manuallyEnteredScalingValues[displayKey].min,
          max: manuallyEnteredScalingValues[displayKey].max,
          displayName: displayName,
          actualUnit: actualUnit
        };
      } else {
        // Use API values as default
        const { min, max } = getMinMaxForUnit(actualUnit);
        initialValues[displayKey] = {
          min: min.toString(),
          max: max.toString(),
          displayName: displayName,
          actualUnit: actualUnit
        };
      }
    });
    
    setUnitScalingValues(initialValues);
    setModifiedUnits(new Set()); // Reset modified units tracking when opening modal
    setShowCustomScalingModal(true);
  };

  const handleCustomScalingClose = () => {
    setShowCustomScalingModal(false);
    setUnitScalingValues({});
    setModifiedUnits(new Set()); // Reset modified units tracking
  };

  // Function to update min/max values for a specific unit
  const handleUnitValueChange = (unit: string, field: 'min' | 'max', value: string) => {
    // Mark this unit as modified
    setModifiedUnits(prev => new Set(prev).add(unit));
    
    // Update current modal values
    setUnitScalingValues(prev => ({
      ...prev,
      [unit]: {
        ...prev[unit],
        [field]: value
      }
    }));
    
    // Also update persistent manually entered values
    setManuallyEnteredScalingValues(prev => ({
      ...prev,
      [unit]: {
        ...prev[unit],
        min: field === 'min' ? value : prev[unit]?.min || '',
        max: field === 'max' ? value : prev[unit]?.max || ''
      }
    }));
  };

  // Helper function to check if apply button should be enabled
  const isApplyButtonEnabled = () => {
    // Check each unit's state
    for (const [unit, values] of Object.entries(unitScalingValues)) {
      const minValue = values.min.trim();
      const maxValue = values.max.trim();
      
      // If min has value but max doesn't, disable button
      if (minValue !== '' && maxValue === '') {
        return false;
      }
      
      // If max has value but min doesn't, disable button  
      if (maxValue !== '' && minValue === '') {
        return false;
      }
      
      // If both have values, check if they're valid
      if (minValue !== '' && maxValue !== '') {
        const min = parseFloat(minValue);
        const max = parseFloat(maxValue);
        if (isNaN(min) || isNaN(max) || max <= min) {
          return false;
        }
      }
    }
    
    // Check if at least one unit has both values
    const hasCompleteUnit = Object.values(unitScalingValues).some(values => {
      const minValue = values.min.trim();
      const maxValue = values.max.trim();
      return minValue !== '' && maxValue !== '';
    });
    
    return hasCompleteUnit;
  };

  const handleCustomScalingApply = () => {
    // Only process units that were actually modified by the user
    const modifiedValidUnits = Object.entries(unitScalingValues).filter(([unit, values]) => {
      // Only include units that were actually modified AND have valid values
      if (!modifiedUnits.has(unit)) return false;
      
      const min = parseFloat(values.min);
      const max = parseFloat(values.max);
      return !isNaN(min) && !isNaN(max) && max > min;
    });
    
    if (modifiedValidUnits.length > 0) {
      // Dispose existing chart if it exists to force complete recreation
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
      }
      
      // Start with existing custom scaling or empty object
      const multiUnitScaling: { [unit: string]: { min: number; max: number } } = {
        ...(customScaling || {}) // Preserve existing custom scaling
      };
      
      // Only update scaling for modified units
      modifiedValidUnits.forEach(([key, values]) => {
        // Use the stored actualUnit from the values object
        const actualUnit = values.actualUnit || key;
        
        multiUnitScaling[actualUnit] = {
          min: parseFloat(values.min),
          max: parseFloat(values.max)
        };
      });
      
      // Set custom scaling (preserving unmodified units)
      setCustomScaling(multiUnitScaling);
      
      // Force chart recreation with new scaling
      setChartCreated(false);
      setLastChartDataKey(''); // Clear the data key to force recreation
      
      // Ensure chart recreates after a brief delay
      setTimeout(() => {
        setChartCreated(false); // Double-check chart recreation
      }, 100);
      

    }
    
    handleCustomScalingClose();
  };

  // Chart mode handlers
  const handleChartModeChange = (mode: 'individual' | 'group') => {
    setChartMode(mode);
    
    if (mode === 'group') {
      // Save current individual selection before switching to group
      if (chartMode === 'individual') {
        setIndividualModeSensors(selectedSensors);
      }
      
      // If we have previously saved group mode sensors, restore them
      if (groupModeSensors.length > 0) {
        setSelectedSensors(groupModeSensors);
        setPendingSensors(groupModeSensors);
        // Trigger data fetch with restored group sensors
        if (groupModeSensors.length > 0) {
          triggerTelemetryFetchWithChannels(groupModeSensors);
        }
      } else {
        // First time in group mode - limit current selection to 3 sensors
        if (selectedSensors.length > 3) {
          const limitedSensors = selectedSensors.slice(0, 3);
          setSelectedSensors(limitedSensors);
          setPendingSensors(limitedSensors);
          // Save these as the group mode sensors
          setGroupModeSensors(limitedSensors);
          // Trigger data fetch with limited sensors
          if (limitedSensors.length > 0) {
            triggerTelemetryFetchWithChannels(limitedSensors);
          }
        } else {
          // Less than or equal to 3 sensors, keep them all for group mode
          setPendingSensors(selectedSensors);
          setGroupModeSensors(selectedSensors);
        }
      }
    } else {
      // Save current group selection before switching to individual
      if (chartMode === 'group') {
        setGroupModeSensors(selectedSensors);
      }
      
      // For individual mode, restore previously saved individual sensors if available
      if (individualModeSensors.length > 0 && chartMode === 'group') {
        setSelectedSensors(individualModeSensors);
        setPendingSensors(individualModeSensors);
        // Trigger data fetch with restored sensors
        if (individualModeSensors.length > 0) {
          triggerTelemetryFetchWithChannels(individualModeSensors);
        }
      } else {
        // Keep current sensors if no saved individual selection
        setPendingSensors(selectedSensors);
      }
    }
    
    // Reset chart when switching modes
    setChartCreated(false);
    setLastChartDataKey('');
  };

  // API function to fetch asset types (keeping original for fallback)
  const fetchAssetTypes = async (): Promise<SensorData[]> => {
    try {
      setLoading(true);
      const assetTypesResponse = await getAssetTypes();

      const allSensors: SensorData[] = [];
      let colorIndex = 0;

      // Handle the API response structure which has Data property
      const assetTypes = assetTypesResponse?.Data || assetTypesResponse;

      if (Array.isArray(assetTypes)) {
        assetTypes.forEach((assetType: any) => {
          if (assetType.parameters && Array.isArray(assetType.parameters)) {
            assetType.parameters.forEach((parameter: any) => {

              // Check if this parameter has sensor data
              if (parameter.sensorName && (parameter.channelId || parameter.tag)) {
                const channelId = parameter.channelId || parameter.tag;

                // Normalize unit to a display string. The API may return units as strings
                // or as objects like { code, description } — ensure we never store an object
                // on the sensor.unit property (React can't render objects as children).
                let unit = '';
                if (parameter.units && parameter.units.length > 0) {
                  const firstUnit = parameter.units[0];
                  if (typeof firstUnit === 'string') {
                    unit = firstUnit;
                  } else if (firstUnit && typeof firstUnit === 'object') {
                    // Prefer unit code, fall back to description or JSON
                    unit = firstUnit.code || firstUnit.description || JSON.stringify(firstUnit);
                  } else {
                    unit = String(firstUnit);
                  }
                }

                // Normalize unit string by trimming whitespace and handling null/undefined
                unit = unit.trim();

                // Handle empty or undefined units
                if (!unit || unit === '' || unit === 'undefined' || unit === 'null') {
                  unit = ''; // Keep empty for sensors that truly have no unit
                }

                // Create a unique sensor identifier to avoid duplicates
                const sensorKey = `${channelId}_${parameter.sensorName}_${assetType.typeName}`;

                // Check if we already have this sensor to avoid duplicates
                // Use a more specific check that includes asset type and position
                const existingSensor = allSensors.find(s =>
                  s.channelId === channelId &&
                  s.sensorName === parameter.sensorName &&
                  s.assetType === assetType.typeName
                );
                if (!existingSensor) {
                  allSensors.push({
                    id: sensorKey, // Unique identifier for React keys
                    channelId: channelId,
                    sensorName: parameter.sensorName,
                    unit: unit,
                    color: colorPalette[colorIndex % colorPalette.length],
                    dataPoints: [], // Will be populated when actual data is fetched
                    assetType: assetType.typeName // Add asset type to each sensor
                  });

                  colorIndex++;
                }
              }
            });
          }
        });
      }

      // Filter sensors by assetType if provided
      const filteredSensors = assetType
        ? allSensors.filter(sensor => sensor.assetType === assetType)
        : allSensors;



      return filteredSensors;
    } catch (error) {
      // Return empty array in case of error
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Function to create sensors from asset details attributes
  const createSensorsFromAssetDetails = (attributes: AssetAttribute[]): SensorData[] => {
    const sensors: SensorData[] = [];
    let colorIndex = 0;


    attributes.forEach((attribute: AssetAttribute) => {
      // Only create sensors for attributes that have channelId (indicating they are sensor parameters)
      if (attribute.channelId && attribute.name) {
        sensors.push({
          id: `${attribute.channelId}-${colorIndex}`,
          channelId: attribute.channelId,
          sensorName: attribute.name,
          unit: attribute.unit || attribute.unitDescription || '',
          color: colorPalette[colorIndex % colorPalette.length],
          dataPoints: [],
          assetType: assetType || 'unknown'
        });
        colorIndex++;
      }
    });

    return sensors;
  };

  // Fetch sensors on component mount and when assetType or assetParameters change
  useEffect(() => {
    const loadSensors = async () => {
      // Reset data loading states when sensors are being reloaded
      setHasNoData(false);
      setInitialDataLoadComplete(false);
      setSensorsInitialized(false);

      let fetchedSensors: SensorData[];

      if (assetParameters && assetParameters.length > 0) {
        fetchedSensors = createSensorsFromAssetDetails(assetParameters);
      }
      else if (assetId) {
        fetchedSensors = await fetchAssetSensors(assetId);
      }
      else {
        fetchedSensors = await fetchAssetTypes();
      }

      setSensors(fetchedSensors);

      // Auto-select first 3 sensors with different channel IDs if any sensors were loaded
      if (fetchedSensors.length > 0) {
        // Get unique channel IDs to ensure variety in initial selection
        const channelIds = fetchedSensors.map(sensor => sensor.channelId);
        const uniqueChannelIds = channelIds.filter((id, index) => channelIds.indexOf(id) === index);
        const initialSelection = uniqueChannelIds.slice(0, 3);
        setSelectedSensors(initialSelection);
        setPendingSensors(initialSelection); // Initialize pending sensors
        setSensorsInitialized(true); // Mark as initialized
      } else {
        setHasNoData(true);
        setInitialDataLoadComplete(true);
        setSensorsInitialized(true); // Mark as initialized even with no sensors
      }
    };

    loadSensors();
  }, [assetId, assetType, assetParameters]); // Add assetId as dependency

  const getTimezoneOffsetMs = (timeZone: string, date: Date) => {
  const utcDate = new Date(
    date.toLocaleString("en-US", { timeZone: "UTC" })
  );
  const tzDate = new Date(
    date.toLocaleString("en-US", { timeZone })
  );
  return tzDate.getTime() - utcDate.getTime();
};

  // Function to get date range based on selection - NO TIMEZONE CONVERSION
const getDateRange = () => {
  // Simple approach: always use current time for export
  const now = new Date();
  let startMs: number;
  let endMs: number = now.getTime();


  //  Custom range (keep existing behavior)
  if (
    selectedTimeRange === 'Custom date range' &&
    (startDate || appliedStartDate) &&
    (endDate || appliedEndDate)
  ) {
    const effectiveStartDate = startDate || appliedStartDate!;
    const effectiveEndDate = endDate || appliedEndDate!;
    

    return {
      startTime: formatLocalDateTime(effectiveStartDate),
      endTime: formatLocalDateTime(effectiveEndDate),
    };
  }

  //  Calculate relative ranges properly
  switch (selectedTimeRange) {
    case 'Last 1 hour':
      startMs = endMs - (1 * 60 * 60 * 1000);
      break;
    case 'Last 2 hours':
      startMs = endMs - (2 * 60 * 60 * 1000);
      break;
    case 'Last 4 hours':
      startMs = endMs - (4 * 60 * 60 * 1000);
      break;
    case 'Last 8 hours':
      startMs = endMs - (8 * 60 * 60 * 1000);
      break;
    case 'Last 24 hours':
      startMs = endMs - (24 * 60 * 60 * 1000);
      break;
    case 'Last 7 days':
      startMs = endMs - (7 * 24 * 60 * 60 * 1000);
      break;
    default:
      startMs = endMs - (1 * 60 * 60 * 1000); // Default to 1 hour
  }

  // Convert epoch to string (same contract as before)
  const result = {
    startTime: formatLocalDateTime(new Date(startMs)),
    endTime: formatLocalDateTime(new Date(endMs)),
  };
  
  return result;
};

  // Helper function to format date as local datetime string without timezone conversion
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    const result = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    // Return in ISO-like format but with local time (no 'Z' suffix)
    return result;
  };

  // Helper function to parse local datetime string without timezone conversion
  const parseLocalDateTime = (dateTimeString: string): number => {
    // Remove any timezone indicators and parse as local time
    const cleanDateString = dateTimeString.replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');

    // Parse as local time by creating Date object from components
    const parts = cleanDateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/);
    if (parts) {
      const [, year, month, day, hours, minutes, seconds, ms] = parts;
      const localDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds),
        parseInt(ms || '0')
      );
      return localDate.getTime();
    }

    // Fallback to regular parsing if format doesn't match
    return new Date(dateTimeString).getTime();
  };
      const getRelativeTimeValue = (): number=> {
      switch (selectedTimeRange) {
        case 'Last 1 hour':
          return 0;
        case 'Last 2 hours':
          return 1;
        case 'Last 4 hours':
          return 2;
        case 'Last 8 hours':
          return 3;
        case 'Last 24 hours':
          return 4;
        case 'Last 7 days':
          return 5;
        default:
          return 6; // Custom date range
      }
    };

  // Calendar modal handlers
  const handleCalendarToggle = () => {
    if (!showCalendarModal) {
      // When opening the modal, calculate and set dates based on current time range
      if (appliedStartDate && appliedEndDate) {
        setStartDate(appliedStartDate);
        setEndDate(appliedEndDate);
      } else {
        // Calculate dates based on current selected time range
        const { startTime, endTime } = getDateRange();
        const calculatedStartDate = new Date(startTime);
        const calculatedEndDate = new Date(endTime);
        setStartDate(calculatedStartDate);
        setEndDate(calculatedEndDate);
      }
      // Reset sidebar option tracking when opening modal
      setSelectedSidebarOption(null);
    }
    setShowCalendarModal(!showCalendarModal);
  };

  const handleDateRangeApply = () => {
    // Reset chart flag since date range is changing
    setChartCreated(false);
    setHasValidChartData(false);
    setChartDataSnapshot([]); // Clear snapshot when changing date range
    setCustomScaling(null); // Reset custom scaling when date range changes
    setManuallyEnteredScalingValues({}); // Clear manually entered scaling values when date range changes
  };
// Function to get timezone short name for display
// Simple function to get timezone short name from localStorage
const getTimezoneShortName = (): string => {
  // Directly get from localStorage where UserPreferences saved it
  const timezoneShort = localStorage.getItem('userTimezoneShort');
  
  // If not found in localStorage, use an empty string or fallback
  return timezoneShort || ''; // Return empty string if not set
};
  // Function to fetch telemetry data for selected sensors
  
const fetchTelemetryData = async (channelIds: string[]): Promise<void> => {
  if (channelIds.length === 0) return;

  // Validate assetId before making API call
  if (!assetId || assetId.trim() === '') {
    setHasNoData(true);
    setInitialDataLoadComplete(true);
    return;
  }

  if (!isValidGuid(assetId)) {
    setHasNoData(true);
    setInitialDataLoadComplete(true);
    return;
  }

  // Prevent multiple simultaneous calls
  if (telemetryFetchingRef.current) {
    return;
  }

  try {
    telemetryFetchingRef.current = true;
    setChartDataLoading(true);

    const { startTime, endTime } = getDateRange();
    const Timerange = getRelativeTimeValue();
    // Use the browserTimezone state variable OR get it directly
    const userTimezone = browserTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    //     telemetryRequest.timeRange = Timerange;
    //     telemetryRequest.StartTime = startTime;
    //     telemetryRequest.EndTime = endTime;
      const telemetryRequest: TelemetryQueryRequest = {
        AssetId: assetId,
        ChannelIds: channelIds,
        timeRange:Timerange,
        };

      if (selectedTimeRange === 'Custom date range') {
        const { startTime, endTime } = getDateRange();
        telemetryRequest.StartTime = startTime;
        telemetryRequest.EndTime = endTime;
      } else {
        telemetryRequest.timeRange = Timerange;
      }


    // ACTUALLY CALL THE API FUNCTION
    const telemetryData = await queryTelemetryData(telemetryRequest);

    // Handle API response structure - the data is in the Data.telemetryData property
    if (telemetryData.Success && telemetryData.Data && telemetryData.Data.telemetryData) {

      // Extract and store uomRanges from API response
      if (telemetryData.Data.uomRanges && Array.isArray(telemetryData.Data.uomRanges)) {
        setUomRanges(telemetryData.Data.uomRanges);
      }

      // Analyze available data time range for better user experience
      const allApiDataPoints: { timestamp: string; channelId: string }[] = [];
      telemetryData.Data.telemetryData.forEach((channel: any) => {
        if (channel.data && channel.data.length > 0) {
          channel.data.forEach((point: any) => {
            allApiDataPoints.push({ timestamp: point.timestamp, channelId: channel.channelId });
          });
        }
      });

      if (allApiDataPoints.length > 0) {
        const apiTimestamps = allApiDataPoints.map(p => parseLocalDateTime(p.timestamp));
        const apiDataStart = Math.min(...apiTimestamps);
        const apiDataEnd = Math.max(...apiTimestamps);

        // Check if requested range overlaps with available data
        const requestTimeRange = getDateRange();
        const requestStart = parseLocalDateTime(requestTimeRange.startTime);
        const requestEnd = parseLocalDateTime(requestTimeRange.endTime);
        const hasOverlap = !(apiDataEnd < requestStart || apiDataStart > requestEnd);

        if (!hasOverlap) {
          if (requestStart > apiDataEnd) {

          }
        }
      }

      // Get requested time range in ms - handle local datetime strings
      const { startTime, endTime } = getDateRange();
      // Parse as local time (no automatic UTC conversion)
      const startMs = parseLocalDateTime(startTime);
      const endMs = parseLocalDateTime(endTime);

      // Update sensors with real data, filtered by requested time range
      const updatedSensors = sensors.map(sensor => {
        if (channelIds.includes(sensor.channelId)) {
          // Find the channel data for this specific sensor in the telemetryData array
          const channelData = telemetryData.Data.telemetryData?.find((channel: any) => channel.channelId === sensor.channelId);
          if (channelData && channelData.data) {
            // Log sample raw data from API to debug timezone issues
            if (channelData.data.length > 0) {
              const sampleApiPoint = channelData.data[0];
              const expectedTimestamp = new Date('2025-12-05T00:47:48.000Z').getTime(); // Test with known good timestamp
              const actualTimestamp = new Date(sampleApiPoint.timestamp).getTime();

            }

            // Process the nested data array treating all timestamps as local time
            const allPoints = channelData.data
              .filter((point: any) => point.value !== null && point.value !== undefined)
              .map((point: any) => {
                // Parse timestamp as local time (no timezone conversion)
                let timestamp = point.timestamp;

                // Convert API timestamp to local time format
                const parsedTimeMs = parseLocalDateTime(timestamp);
                if (isNaN(parsedTimeMs)) {
                  console.warn(`Invalid timestamp: ${timestamp}`);
                  return null;
                }

                // Convert back to local datetime string format for consistency
                const localDateTime = formatLocalDateTime(new Date(parsedTimeMs));

                return {
                  date: localDateTime,
                  value: point.value as number
                };
              })
              .filter((point: any): point is { date: string; value: number } => point !== null);

            // Filter points to only those within requested time range (local time)
                    let sensorDataPoints = allPoints.filter(dp => {
              const ts = parseLocalDateTime(dp.date);
              return ts >= startMs && ts <= endMs;
              });

              // IF UI range-la data illa, API data irundhaa show pannum
              if (sensorDataPoints.length === 0 && allPoints.length > 0) {
              sensorDataPoints = allPoints;
              }


            // If no data found in exact range, consider showing available data with a warning
            if (sensorDataPoints.length === 0 && allPoints.length > 0) {
              // Check if we're close to the available data (within reasonable bounds)
              const dataTimestamps = allPoints.map((dp: { date: string; value: number }) => parseLocalDateTime(dp.date));
              const dataStart = Math.min(...dataTimestamps);
              const dataEnd = Math.max(...dataTimestamps);
              const timeDiffHours = Math.abs(startMs - dataEnd) / (1000 * 60 * 60);

              // If the requested time is within 12 hours of available data, show the available data
              if (timeDiffHours <= 12) {
                sensorDataPoints = allPoints; // Show all available data
              }
            }
            // Log filtering results for debugging
            const dataTimestamps = allPoints.map((dp: { date: string; value: number }) => parseLocalDateTime(dp.date));
            const minDataTime = dataTimestamps.length > 0 ? Math.min(...dataTimestamps) : 0;
            const maxDataTime = dataTimestamps.length > 0 ? Math.max(...dataTimestamps) : 0;

            // Log min/max timestamps for this sensor
            if (sensorDataPoints.length > 0) {
              const timestamps = sensorDataPoints.map((dp: { date: string; value: number }) => new Date(dp.date).getTime());
              const minTs = new Date(Math.min(...timestamps));
              const maxTs = new Date(Math.max(...timestamps));

            } else {
              const dataShown = sensorDataPoints.length > 0;
              if (dataShown) {

              } else {

              }

              // Show why no data was found
              if (!dataShown && allPoints.length > 0 && allPoints[0] && allPoints[allPoints.length - 1]) {
                const firstPoint = new Date(allPoints[0].date).getTime();
                const lastPoint = new Date(allPoints[allPoints.length - 1].date).getTime();
                const requestStart = startMs;
                const requestEnd = endMs;
                const hoursOffset = (firstPoint - requestEnd) / (1000 * 60 * 60);

              }
            }

            // Add metadata to track if we're showing adjusted data
            const isAdjustedData = sensorDataPoints.length > 0 &&
              (sensorDataPoints.length === allPoints.length) &&
              allPoints.length > 0;

            if (isAdjustedData) {
              const dataStart = new Date(Math.min(...sensorDataPoints.map((dp: { date: string; value: number }) => new Date(dp.date).getTime())));
              const dataEnd = new Date(Math.max(...sensorDataPoints.map((dp: { date: string; value: number }) => new Date(dp.date).getTime())));

            }

            return {
              ...sensor,
              dataPoints: sensorDataPoints, // This is already properly typed as { date: string; value: number }[]
              unit: channelData.uom || sensor.unit,
              minValue: channelData.minValue,
              maxValue: channelData.maxValue,
              // Add threshold data from API response
              alarmHData: channelData.alarmHData || [],
              alarmHHData: channelData.alarmHHData || [],
              alarmLData: channelData.alarmLData || [],
              alarmLLData: channelData.alarmLLData || []
            } as SensorData;
          } else {
            return {
              ...sensor,
              dataPoints: []
            } as SensorData;
          }
        }
        return sensor;
      });

      // Check if any of the selected sensors have data
      const hasAnyData = updatedSensors
        .filter(s => channelIds.includes(s.channelId))
        .some(s => s.dataPoints && s.dataPoints.length > 0);

      // Check if we're showing adjusted data and set a flag for UI notification
      const showingAdjustedData = hasAnyData && allApiDataPoints.length > 0 && (() => {
        const apiTimestamps = allApiDataPoints.map(p => new Date(p.timestamp).getTime());
        const apiDataStart = Math.min(...apiTimestamps);
        const apiDataEnd = Math.max(...apiTimestamps);
        const requestStart = new Date(startTime).getTime();
        const requestEnd = new Date(endTime).getTime();
        return apiDataEnd < requestStart || apiDataStart > requestEnd;
      })();

      if (showingAdjustedData) {
        const apiTimestamps = allApiDataPoints.map(p => new Date(p.timestamp).getTime());
        const apiDataStart = new Date(Math.min(...apiTimestamps));
        const apiDataEnd = new Date(Math.max(...apiTimestamps));

      }

      // If no data found, provide helpful message about data availability
      if (!hasAnyData && allApiDataPoints.length > 0) {
        const apiTimestamps = allApiDataPoints.map(p => new Date(p.timestamp).getTime());
        const apiDataStart = new Date(Math.min(...apiTimestamps));
        const apiDataEnd = new Date(Math.max(...apiTimestamps));

      }

      // Batch all state updates together to prevent multiple re-renders
      setSensors(updatedSensors);
      setHasNoData(!hasAnyData);
      setInitialDataLoadComplete(true);
      setHasValidChartData(hasAnyData); // Track if we have valid data

      // Save chart data snapshot when we have valid data
      if (hasAnyData) {
        const validSensors = updatedSensors.filter(s =>
          channelIds.includes(s.channelId) && s.dataPoints && s.dataPoints.length > 0
        ) as SensorData[];
        setChartDataSnapshot(validSensors);
      }
    } else {
      // Mark as no data if API call was not successful
      setHasNoData(true);
      setInitialDataLoadComplete(true);
    }
  } catch (error) {
    // Keep existing mock data if telemetry fetch fails
    console.error('Error fetching telemetry data:', error);
    setHasNoData(true);
    setInitialDataLoadComplete(true);
  } finally {
    telemetryFetchingRef.current = false; // Reset the flag
    setChartDataLoading(false);
  }
};
  const applyLastOneHour = () => {
  // UI state reset
  setSelectedTimeRange('Last 1 hour');

  const now = new Date();
  const oneHourAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000);

  setStartDate(oneHourAgo);
  setEndDate(now);
  setAppliedStartDate(oneHourAgo);
  setAppliedEndDate(now);

  // Chart & scaling reset
  setChartCreated(false);
  setHasValidChartData(false);
  setChartDataSnapshot([]);
  setCustomScaling(null);
  setManuallyEnteredScalingValues({});

  // 🔥 ACTUAL API CALL
  triggerTelemetryFetch();
};

  // Manual function to fetch telemetry data (called only on Apply)
  const triggerTelemetryFetch = () => {
    // Clear any existing timeout
    if (telemetryTimeoutRef.current) {
      clearTimeout(telemetryTimeoutRef.current);
    }

    // Reset no data state when starting new fetch
    setHasNoData(false);
    setInitialDataLoadComplete(false);

    // Validate assetId first
    if (!assetId || assetId.trim() === '') {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    if (!isValidGuid(assetId)) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    // Only fetch if we have both sensors loaded and selected sensors
    if (sensors.length === 0 || selectedSensors.length === 0) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    // Debounce the telemetry call to prevent rapid successive calls
    telemetryTimeoutRef.current = setTimeout(() => {
      const selectedChannelIds = selectedSensors.filter(channelId => {
        return sensors.some(sensor => sensor.channelId === channelId);
      });

      // Get unique channel IDs to avoid sending duplicates to the API
      const uniqueChannelIds = selectedChannelIds.filter((id, index) => selectedChannelIds.indexOf(id) === index);

      if (uniqueChannelIds.length > 0) {
        fetchTelemetryData(uniqueChannelIds);
      }
    }, 300); // 300ms debounce delay
  };

  // Function to fetch telemetry with specific channels (used when applying filter)
  const triggerTelemetryFetchWithChannels = (channelIds: string[]) => {
    // Clear any existing timeout
    if (telemetryTimeoutRef.current) {
      clearTimeout(telemetryTimeoutRef.current);
    }

    // Reset no data state when starting new fetch (always reset for explicit apply)
    setHasNoData(false);
    setInitialDataLoadComplete(false);

    // Validate assetId first
    if (!assetId || assetId.trim() === '') {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    if (!isValidGuid(assetId)) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    // Only fetch if we have both sensors loaded and channel IDs provided
    if (sensors.length === 0 || channelIds.length === 0) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    // Debounce the telemetry call to prevent rapid successive calls
    telemetryTimeoutRef.current = setTimeout(() => {
      const selectedChannelIds = channelIds.filter(channelId => {
        return sensors.some(sensor => sensor.channelId === channelId);
      });

      // Get unique channel IDs to avoid sending duplicates to the API
      const uniqueChannelIds = selectedChannelIds.filter((id, index) => selectedChannelIds.indexOf(id) === index);

      if (uniqueChannelIds.length > 0) {
        fetchTelemetryData(uniqueChannelIds);
      }
    }, 300); // 300ms debounce delay
  };

  // Only fetch telemetry on initial load and datetime/asset changes (not sensor selection changes)
  useEffect(() => {
    // Only trigger after sensors are initialized to prevent multiple loads
    if (sensorsInitialized && sensors.length > 0 && selectedSensors.length > 0) {
      triggerTelemetryFetch();
    } else if (sensorsInitialized && selectedSensors.length === 0) {
      // If no sensors are selected, reset to show selection message
      setHasNoData(false);
      setInitialDataLoadComplete(true);
    }
  }, [assetId, selectedTimeRange, startDate, endDate, appliedStartDate, appliedEndDate, sensorsInitialized]); // Added appliedStartDate and appliedEndDate to dependencies

  // Effect to handle chart visibility when component becomes visible (e.g., tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);

      if (isVisible && chartInstanceRef.current) {
        // Page became visible, resize chart to ensure proper display
        setTimeout(() => {
          if (chartInstanceRef.current && chartInstanceRef.current.root) {
            chartInstanceRef.current.root.resize();
          }
        }, 150);
      }
    };

    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check when the component mounts
    if (chartInstanceRef.current) {
      setTimeout(() => {
        if (chartInstanceRef.current && chartInstanceRef.current.root) {
          chartInstanceRef.current.root.resize();
        }
      }, 150);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chartCreated]); // Depend on chartCreated to run when chart is available

  // Effect to handle when the analysis tab becomes visible within the application
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && chartInstanceRef.current) {
          // Chart container is visible, resize to ensure proper display without recreating
          setTimeout(() => {
            if (chartInstanceRef.current && chartInstanceRef.current.root) {
              chartInstanceRef.current.root.resize();
            }
          }, 100);
        }
      },
      { threshold: 0.1 }
    );

    const chartContainer = document.getElementById('analysis-chartdiv');
    if (chartContainer) {
      observer.observe(chartContainer);
    }

    return () => {
      observer.disconnect();
      if (chartContainer) observer.unobserve(chartContainer);
    };
  }, [chartCreated]);

  // Additional effect to handle tab switching and component visibility
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      if (chartInstanceRef.current && chartInstanceRef.current.root) {
        // Clear any existing timeout to debounce resize calls
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }

        // Debounce resize to avoid rapid calls during tab switches
        resizeTimeout = setTimeout(() => {
          if (chartInstanceRef.current && chartInstanceRef.current.root) {
            chartInstanceRef.current.root.resize();
          }
        }, 150);
      }
    };

    // Listen for window resize events (can be triggered by tab switching)
    window.addEventListener('resize', handleResize);

    // Also trigger a resize when the chart is first created to ensure proper display
    if (chartCreated && chartInstanceRef.current) {
      handleResize();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [chartCreated]);

  // Filter sensors based on search input
  const searchFiltered = sensors.filter(sensor =>
    sensor.sensorName.toLowerCase().includes(searchInputVal.toLowerCase())
  );

  const handleFilterDropdownClick = () => {
    if (!showFilterPopup) {
      // Initialize pending sensors with current selections when opening
      setPendingSensors(selectedSensors);
      setSearchInputVal(''); // Clear search field when opening dropdown
    }
    setShowFilterPopup(!showFilterPopup);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputVal(e.target.value);
  };

  const handleClearButton = () => {
    setPendingSensors([]);
    // Don't close the modal, let user continue selecting or click Apply
  };

  const handleApplyButton = () => {
    // Reset chart created flag since we're changing sensors
    setChartCreated(false);
    setHasValidChartData(false);
    setChartDataSnapshot([]); // Clear snapshot when changing sensors
    setCustomScaling(null); // Reset custom scaling when sensors change
    setManuallyEnteredScalingValues({}); // Clear manually entered scaling values when sensors change

    // Apply the pending sensor selections immediately
    setSelectedSensors(pendingSensors);
    
    // Save the sensor selection for the current chart mode
    if (chartMode === 'group') {
      setGroupModeSensors(pendingSensors);
    } else {
      setIndividualModeSensors(pendingSensors);
    }
    
    setShowFilterPopup(false);

    // Trigger API call with new selections directly (don't wait for state update)
    if (pendingSensors.length > 0) {
      triggerTelemetryFetchWithChannels(pendingSensors);
    } else {
      // If no sensors selected, show appropriate message
      setHasNoData(false);
      setInitialDataLoadComplete(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside filter dropdown
      const isClickInsideFilter = filterRef.current?.contains(target);
      // Check if click is inside calendar modal
      const isClickInsideCalendar = calendarModalRef.current?.contains(target);
      // Check if click is inside color picker
      const isClickInsideColorPicker = colorPickerRef.current?.contains(target);

      // Only close filter popup if click is outside AND not inside color picker
      if (showFilterPopup && !isClickInsideFilter && !isClickInsideColorPicker) {
        setShowFilterPopup(false);
      }

      // Only close calendar modal if click is outside
      if (showCalendarModal && !isClickInsideCalendar) {
        setShowCalendarModal(false);
      }

      // Only close color picker if click is outside AND not inside filter dropdown
      if (showColorPickerFor && !isClickInsideColorPicker && !isClickInsideFilter) {
        setShowColorPickerFor(null);
        setColorPickerPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPopup, showCalendarModal, showColorPickerFor]);

  const handleSensorChange = (channelId: string) => {
    setPendingSensors((prev) => {
      if (prev.includes(channelId)) {
        return prev.filter((id) => id !== channelId);
      } else {
        // Apply maximum 3 sensors limit only for group mode
        if (chartMode === 'group' && prev.length >= 3) {
          return prev;
        }
        return [...prev, channelId];
      }
    });
  };

  const getSelectedSensorData = () => {
    const currentData = sensors.filter(sensor => selectedSensors.includes(sensor.channelId));

    // If we have valid current data, use it
    if (currentData.length > 0 && currentData.some(s => s.dataPoints && s.dataPoints.length > 0)) {
      return currentData;
    }

    // If we don't have current data but have a snapshot and valid chart, use snapshot
    if (chartDataSnapshot.length > 0 && hasValidChartData && selectedSensors.length > 0) {
      return chartDataSnapshot.filter(sensor => selectedSensors.includes(sensor.channelId));
    }

    return currentData;
  };

  // Memoize chart data to prevent unnecessary re-renders
  const chartDataKey = React.useMemo(() => {
    const selectedData = getSelectedSensorData();
    const sensorsWithData = selectedData.filter(sensor => sensor.dataPoints && sensor.dataPoints.length > 0);
    const dataHash = sensorsWithData.map(sensor =>
      `${sensor.channelId}-${sensor.dataPoints.length}-${sensor.unit}`
    ).join('|');
    
    // Include custom scaling in the key so chart recreates when scaling changes
    const scalingKey = customScaling ? 
      Object.entries(customScaling).map(([unit, values]) => `${unit}-${values.min}-${values.max}`).join('|') 
      : 'auto';
    
    // Exclude hasNoData and initialDataLoadComplete from the key to prevent unnecessary chart disposal
    return `${selectedSensors.join(',')}-${dataHash}-${selectedTimeRange}-${startDate?.getTime()}-${endDate?.getTime()}-${scalingKey}`;
  }, [selectedSensors, sensors, selectedTimeRange, startDate, endDate, customScaling]);

function createSeries(
  chart: am5xy.XYChart,
  root: am5.Root,
  name: string,
  color: string,
  xAxis: am5xy.DateAxis<am5xy.AxisRendererX>,
  yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>,
  data: any[],
  cursor: any,
  index: number,
  allSameScale: boolean = false,
  sensorData?: SensorData // Add sensor data parameter for dynamic detection
) {
  // Helper function to dynamically determine if sensor is a relay
  const isRelaySensor = (sensorName: string, sensorData?: SensorData): boolean => {
    if (sensorData) {
      // Use dynamic detection if sensor data is available
      const hasOnlyBinaryValues = sensorData.dataPoints.every(point => point.value === 0 || point.value === 1);
      const measurementUnits = ['°c', '°f', '%', 'ppm', 'mg/l', 'ph', 'mv', 'ma', 'v', 'a', 'w', 'bar', 'psi', 'l/h', 'm³/h', 'gpm'];
      const hasNoMeasurementUnit = !sensorData.unit || !measurementUnits.includes(sensorData.unit.toLowerCase());
      const hasRelayNaming = sensorName.toLowerCase().includes('relay') || sensorName.toLowerCase().includes('pump') || 
                             sensorName.toLowerCase().includes('valve') || sensorName.toLowerCase() === 'unassigned';
      
      return (hasOnlyBinaryValues && hasNoMeasurementUnit) || hasRelayNaming;
    } else {
      // Fallback to name-based detection
      const relayKeywords = ['relay', 'acid', 'pump', 'valve'];
      return relayKeywords.some(keyword => 
        sensorName.toLowerCase().includes(keyword.toLowerCase())
      ) || sensorName.toLowerCase() === 'unassigned';
    }
  };

  // Determine if this sensor should use step line chart
  const useStepLine = isRelaySensor(name, sensorData);

  // --- Create Series (StepLineSeries for relays, LineSeries for others) ---
  const series = useStepLine ?
    chart.series.push(
      am5xy.StepLineSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stacked: false,
        connect: false,
        noRisers: false, // Show vertical connectors for step line
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'left',
          y: am5.percent(50),
          forceHidden: true,
        }),
        snapTooltip: true,
        stroke: am5.color(color),
        fill: am5.color(color),
      }),
    ) :
    chart.series.push(
      am5xy.LineSeries.new(root, {
        name: name,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        stacked: false,
        connect: false, //Connect only consecutive valid points
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'left',
          y: am5.percent(50),
          forceHidden: true,
        }),
        snapTooltip: true,
        stroke: am5.color(color),
        fill: am5.color(color),
      }),
    );

  // --- Styling ---
  series.strokes.template.set('strokeWidth', useStepLine ? 3 : 2); // Thicker lines for relay step series
  series.fills.template.setAll({ fillOpacity: 0, visible: false });

  // --- Bullets ---
  series.bullets.push(() => {
    const circle = am5.Circle.new(root, {
      radius: 0,
      fill: am5.color('#000'),
      stroke: am5.color('#fff'),
      strokeWidth: 3,
      interactive: true,
      opacity: 0,
    });
    circle.states.create('default', { opacity: 0 });
    circle.states.create('hover', { radius: 6, opacity: 1 });
    return am5.Bullet.new(root, { locationX: 0.5, sprite: circle });
  });

  // --- Cursor tooltip handling ---
  let previousBulletSprites: any = [];
  function cursorMoved() {
    for (let i = 0; i < previousBulletSprites?.length; i++) {
      previousBulletSprites[i]?.unhover();
    }
    previousBulletSprites = [];
    chart.series.each(function (series) {
      const dataItem = series?.get('tooltip')?.dataItem;
      if (dataItem) {
        const bulletSprite = dataItem?.bullets?.length && dataItem?.bullets[0]?.get('sprite');
        bulletSprite && bulletSprite.hover();
        previousBulletSprites.push(bulletSprite);
      }
    });

    const cursorPoint = cursor.getPrivate("point");
    const chartWidth = chart.width();
    const tooltip = chart.plotContainer.get("tooltip");
    const toolTipPosition = tooltip?.get("pointerOrientation");
    if (cursorPoint) {
      const xPos = cursorPoint.x;
      if (xPos < chartWidth / 3) {
        if (toolTipPosition !== "left") {
          tooltip?.set("pointerOrientation", "left");
          tooltip?.set('dx', 15);
        }
      } else {
        if (toolTipPosition !== "right") {
          tooltip?.set("pointerOrientation", "right");
          tooltip?.set('dx', -15);
        }
      }
    }

    // Tooltip HTML content
    let show = true;
    let html = `<div style="width:auto;display:flex;flex-direction:column;font-size: 13px;color: #F7F9F9;font-family: 'Inter';font-weight: 400">`;
    chart.series.each(function (series) {
      const tooltipDataItem = series.get('tooltipDataItem');
      if (tooltipDataItem) {
        if (show) {
          const valueX = tooltipDataItem.get("valueX");
          if (valueX != null) {
            const date: any = new Date(valueX);
            const dateOptions: any = { weekday: 'long', month: 'short', day: 'numeric' };
            const formattedDate = date.toLocaleDateString(undefined, dateOptions);
            const timezoneShortName = getTimezoneShortName();
            const timeOptions: any = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
            const formattedTime = date.toLocaleTimeString(undefined, timeOptions);
            const result = `${formattedDate}, ${formattedTime}, ${timezoneShortName}`;
            html += `<div style="border-bottom: 1px solid #4A5463;padding:10px"> ${result}</div>`;
            show = false;
          }
        }

        const valueY = tooltipDataItem.get('valueY');
        const seriesName = series.get('name') || 'Unnamed Series';
        const seriesColor = series.get('stroke')?.toString() || '#000000';
        if (series.isVisible() === true) {
          const baseSensorName = seriesName.replace(/\s*\([^)]*\)\s*$/, '');
          const sensor = sensors.find(s => s.sensorName === baseSensorName);
          const unit = sensor?.unit || '';
          const individualValue = valueY == null ? '' : (+valueY).toFixed(2) + (unit ? ' ' + unit : '');
          html += `<div style="display: flex; align-items: center;flex-direction: row;gap: 20px;justify-content: space-between;padding:5px">
            <div style="height:24px;align-items: center;display: flex">
              <span style="border:1px solid ${seriesColor};height:24px;margin-right: 5px;"></span>
              <span>${seriesName}</span>
            </div>
            <div>${individualValue}</div>
          </div>`;
        }
      }
    });
    html += '</div>';
    chart.plotContainer.set('tooltipHTML', html);
  }

  cursor.events.on('cursormoved', cursorMoved);

  // --- Process data and handle gaps > 5 minutes ---
  const GAP_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

  const convertedArray = [...data]?.map((item) => {
    const timestamp = parseLocalDateTime(item.date);
    if (isNaN(timestamp)) return null;
    return { ...item, date: timestamp };
  }).filter((item): item is { date: number; value: number | null } => item !== null);

  const sortedData = convertedArray.sort((a, b) => a.date - b.date);

  // Insert nulls for gaps > 5 minutes at midpoint
  const processedData: { date: number; value: number | null }[] = [];
  for (let i = 0; i < sortedData.length; i++) {
    const current = sortedData[i];
    const previous = i > 0 ? sortedData[i - 1] : null;

    if (previous) {
      const diff = current.date - previous.date;
      if (diff > GAP_THRESHOLD) {
        processedData.push({
        date: previous.date + 1, // 1 ms after previous point
        value: null
       });
 // break line at midpoint
      }
      // Start new segment cleanly
    // processedData.push({
    }
    processedData.push(current);
  }

  series.data.setAll(processedData);

  // --- DateAxis baseInterval ---
  xAxis.set("baseInterval", { timeUnit: "minute", count: 5 });
      const cleandata = sortedData.filter(item =>
      item.value !== null &&
      item.value !== undefined &&
      !isNaN(item.value) &&
      isFinite(item.value)
    );
    series.data.setAll(cleandata);
}

  useLayoutEffect(() => {
    // Only create group charts when in group mode
    if (chartMode !== 'group') {
      return;
    }

    const selectedData = getSelectedSensorData();

    // Filter to only include sensors that have data points
    const sensorsWithData = selectedData.filter(sensor => sensor.dataPoints && sensor.dataPoints.length > 0);

    // For now, use all sensors with data (legend visibility to be implemented later)
    const visibleSensorsWithData = sensorsWithData;

    // Get unique units/sensor names only from sensors with data
    const unitsFromSensorsWithData = visibleSensorsWithData.map(sensor => {
      const unit = sensor.unit?.trim() || '';
      // For sensors without units, use sensor name (consistent with modal behavior)
      return unit === '' ? sensor.sensorName : unit;
    });
    const uniqueUnits = unitsFromSensorsWithData.filter((unit, index) => unitsFromSensorsWithData.indexOf(unit) === index);

    // Check if any visible sensor has data points
    const hasDataToDisplay = visibleSensorsWithData.length > 0;

    // Dispose chart if no data is available after initial load
    if (hasNoData && initialDataLoadComplete) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
        setChartCreated(false);
        setLastChartDataKey('');
      }
      return;
    }
    // Only create chart when we have visible data and loading is complete
    if (uniqueUnits.length > 0 && selectedData.length > 0 && hasDataToDisplay && !hasNoData && !chartDataLoading && initialDataLoadComplete) {
      // Check if chart is already created with the same data
      if (chartCreated && chartInstanceRef.current && chartDataKey === lastChartDataKey) {
        setTimeout(() => {
          if (chartInstanceRef.current && chartInstanceRef.current.root) {
            chartInstanceRef.current.root.resize();
          }
        }, 100);
        return;
      }
      if (chartCreationRef.current) {
        return;
      }
      if (chartCreationTimeoutRef.current) {
        clearTimeout(chartCreationTimeoutRef.current);
      }
      chartCreationTimeoutRef.current = setTimeout(() => {
        chartCreationRef.current = true;
        const containerElement = document.getElementById('analysis-chartdiv');
        if (!containerElement) {
          chartCreationRef.current = false;
          return;
        }
        if (chartInstanceRef.current && chartDataKey !== lastChartDataKey) {
          chartInstanceRef.current.root.dispose();
          chartInstanceRef.current = null;
          setChartCreated(false);
        }
        if (chartInstanceRef.current && chartDataKey === lastChartDataKey) {
          chartCreationRef.current = false;
          return;
        }
        const root = createAmChartsRoot('analysis-chartdiv');
        root.setThemes([am5themes_Animated.new(root)]);
        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panY: false,
            panX: false,
            wheelY: 'zoomX',
            layout: root.verticalLayout,
            maxTooltipDistance: 0,
            paddingTop: 50,
          }),
        );

        // --- ENHANCEMENT: Set x-axis min/max based on actual data or requested range ---
        const { startTime, endTime } = getDateRange();
        let minTime = parseLocalDateTime(startTime);
        let maxTime = parseLocalDateTime(endTime);

        // Check if we have actual data to determine if we should use data range instead
        const allDataTimestamps: number[] = [];
        visibleSensorsWithData.forEach(sensor => {
          if (sensor.dataPoints && sensor.dataPoints.length > 0) {
            sensor.dataPoints.forEach(dp => {
              allDataTimestamps.push(new Date(dp.date).getTime());
            });
          }
        });

        // If we have data and it's outside the requested range, use the data range for axis
        if (allDataTimestamps.length > 0) {
          const dataMinTime = Math.min(...allDataTimestamps);
          const dataMaxTime = Math.max(...allDataTimestamps);

          // Check if data is outside the requested range (indicating adjusted data)
          const dataOutsideRequest = dataMaxTime < minTime || dataMinTime > maxTime;

          if (dataOutsideRequest) {
            minTime = dataMinTime;
            maxTime = dataMaxTime;
          }
        }

        const xAxis = chart.xAxes.push(
          am5xy.DateAxis.new(root, {
            maxDeviation: 0.2,
            baseInterval: { timeUnit: 'second', count: 1 },
            renderer: am5xy.AxisRendererX.new(root, {
              minGridDistance: 25,
              stroke: am5.color('#FFFFFF'),
            }),
            min: minTime,
            max: maxTime,
            strictMinMax: false,
          }),
        );
        xAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));
        xAxis.set("strictMinMax", false);
        xAxis.get("renderer").labels.template.setAll({
          rotation: -45,
          centerY: am5.p100,
          centerX: am5.p100,
          paddingTop: 20,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
        });

        // Add different colors for each day on x-axis labels (only first label of each day)
        const dayColorMap: Record<string, am5.Color> = {};
        const palette = [
          am5.color(0x2563EB), // Day 1 – Blue
          am5.color(0x16A34A), // Day 2 – Green
          am5.color(0xDC2626), // Day 3 – Red
          am5.color(0x7C3AED), // Day 4 – Violet
          am5.color(0xF59E0B), // Day 5 – Amber
          am5.color(0x0EA5E9), // Day 6 – Sky Blue
          am5.color(0xFB7185), // Day 7 – Rose
          am5.color(0x14B8A6), // Day 8 – Teal
          am5.color(0xF97316), // Day 9 – Orange
          am5.color(0x22C55E), // Day 10 – Emerald
          am5.color(0xEF4444), // Day 11 – Light Red
          am5.color(0x3B82F6), // Day 12 – Soft Blue
          am5.color(0x84CC16), // Day 13 – Lime
          am5.color(0xA855F7), // Day 14 – Lavender
          am5.color(0x06B6D4), // Day 15 – Cyan
          am5.color(0xEAB308), // Day 16 – Yellow
          am5.color(0x9333EA), // Day 17 – Purple
          am5.color(0x6366F1), // Day 18 – Indigo
          am5.color(0x10B981), // Day 19 – Mint
          am5.color(0xFACC15), // Day 20 – Gold
          am5.color(0x8B5CF6), // Day 21 – Deep Violet
          am5.color(0x4ADE80), // Day 22 – Light Green
          am5.color(0xF87171), // Day 23 – Salmon
          am5.color(0x60A5FA), // Day 24 – Light Blue
          am5.color(0xA3E635), // Day 25 – Yellow Green
          am5.color(0xC084FC), // Day 26 – Soft Purple
          am5.color(0x38BDF8), // Day 27 – Ice Blue
          am5.color(0x34D399), // Day 28 – Aqua
          am5.color(0xFDBA74), // Day 29 – Peach
          am5.color(0x818CF8), // Day 30 – Periwinkle
          am5.color(0x65A30D), // Day 31 – Olive
          ];


        // helper to get YYYY-MM-DD
        function getDayKey(date: Date) {
          return date.toISOString().split("T")[0];
        }

        const xRenderer = xAxis.get('renderer');
        xRenderer.labels.template.adapters.add("fill", (fill, label) => {
          const dataItem = label.dataItem;
          if (!dataItem) return fill;

          const value = dataItem.get("value" as any);
          if (!value) return fill;

          // Get the previous data item from the axis
          const axis = dataItem.component as am5xy.DateAxis<am5xy.AxisRendererX>;
          const dataItems = axis.dataItems;
          const currentIndex = dataItems.indexOf(dataItem);
          
          if (currentIndex <= 0) {
            // First label - apply day color
            const date = new Date(value as number);
            const key = getDayKey(date);
            if (!dayColorMap[key]) {
              dayColorMap[key] = palette[Object.keys(dayColorMap).length % palette.length];
            }
            return dayColorMap[key];
          }
          
          const prevDataItem = dataItems[currentIndex - 1];
          const prevValue = prevDataItem?.get("value" as any);
          if (!prevValue) return am5.color(0x9ca3af);

          const d1 = new Date(value as number);
          const d2 = new Date(prevValue as number);

          if (d1.getDate() !== d2.getDate()) {
            const key = getDayKey(d1);
            if (!dayColorMap[key]) {
              dayColorMap[key] = palette[Object.keys(dayColorMap).length % palette.length];
            }
            return dayColorMap[key];
          }

          return am5.color(0xffffff); // white for time labels
        });
        const yAxisArray: { [key: string]: am5xy.ValueAxis<am5xy.AxisRendererY> }[] = [];

        // Create scale groups - group units by their effective scale range
        interface ScaleGroup {
          scaleKey: string; // Unique identifier for the scale range
          units: string[]; // Units that share this scale range
          min: number | undefined;
          max: number | undefined;
          hasCustomScaling: boolean;
          hasRelaySensor: boolean;
          representativeUnit: string; // First unit in the group (used for labeling)
        }

        const scaleGroups: ScaleGroup[] = [];

        if (hasDataToDisplay) {
          // Enhanced helper function to determine if sensor is a relay/binary sensor
          const isRelaySensor = (sensorName: string, sensorData?: SensorData): boolean => {
            // First check sensor name for relay-related keywords (case-insensitive)
            const relayKeywords = ['relay', 'acid', 'pump', 'valve', 'switch', 'alarm'];
            const hasRelayKeyword = relayKeywords.some(keyword => 
              sensorName.toLowerCase().includes(keyword.toLowerCase())
            );

            // If sensor data is available, also check for binary/step data patterns
            if (sensorData && sensorData.dataPoints && sensorData.dataPoints.length > 0) {
              // Check if all values are binary (0 or 1) or step-like (only few distinct values)
              const uniqueValues = Array.from(new Set(sensorData.dataPoints.map(point => point.value)));
              const isBinaryData = uniqueValues.every(val => val === 0 || val === 1);
              const isStepData = uniqueValues.length <= 3 && uniqueValues.every(val => val >= 0 && val <= 1.5);
              
              // Also check if sensor has no meaningful unit (indicating it's likely a relay)
              const hasNoMeaningfulUnit = !sensorData.unit || sensorData.unit.trim() === '' || 
                                         sensorData.unit.toLowerCase().includes('state') ||
                                         sensorData.unit.toLowerCase().includes('status');
              
              return hasRelayKeyword || (isBinaryData && hasNoMeaningfulUnit) || 
                     (isStepData && hasNoMeaningfulUnit);
            }

            return hasRelayKeyword;
          };

          // Helper function to get effective scale range for a unit/sensor
          const getEffectiveScaleRange = (unit: string): { min: number | undefined, max: number | undefined, hasCustomScaling: boolean, hasRelaySensor: boolean } => {
            const unitToMatch = unit?.trim() || '';
            const hasCustomScaling = !!(customScaling && customScaling[unitToMatch]);
            
            // Check if any sensor in this unit group is a relay
            const hasRelaySensor = visibleSensorsWithData.some(sensor => {
              const sensorUnit = sensor.unit?.trim() || '';
              const sensorKey = sensorUnit === '' ? sensor.sensorName : sensorUnit;
              return sensorKey === unit && isRelaySensor(sensor.sensorName, sensor);
            });

            let min: number | undefined;
            let max: number | undefined;

            if (hasRelaySensor) {
              min = -0.5;
              max = 1.5;
            } else if (hasCustomScaling && customScaling && customScaling[unitToMatch]) {
              min = customScaling[unitToMatch].min;
              max = customScaling[unitToMatch].max;
            } else {
              // Use uomRanges for automatic scaling
              const uomRange = uomRanges.find(range => range.uom === unitToMatch);
              if (uomRange) {
                min = Math.floor(uomRange.minValue);
                max = Math.ceil(uomRange.maxValue);
              }
            }

            return { min, max, hasCustomScaling, hasRelaySensor };
          };

          // Create scale groups - populate them based on unique units
          uniqueUnits.forEach(unit => {
            const scaleRange = getEffectiveScaleRange(unit);
            
            // For relay sensors, use a special unified scale key to group them all together
            let scaleKey: string;
            if (scaleRange.hasRelaySensor) {
              scaleKey = 'relay-unified--0.5-1.5-false-true'; // All relay sensors share this key
            } else {
              scaleKey = `${scaleRange.min || 'auto'}-${scaleRange.max || 'auto'}-${scaleRange.hasCustomScaling}-${scaleRange.hasRelaySensor}`;
            }
            
            // Find existing group with same scale range
            let existingGroup = scaleGroups.find(group => group.scaleKey === scaleKey);
            
            if (existingGroup) {
              // Add unit to existing group
              existingGroup.units.push(unit);
            } else {
              // Create new scale group
              scaleGroups.push({
                scaleKey,
                units: [unit],
                min: scaleRange.min,
                max: scaleRange.max,
                hasCustomScaling: scaleRange.hasCustomScaling,
                hasRelaySensor: scaleRange.hasRelaySensor,
                representativeUnit: unit
              });
            }
          });

          // Check if all sensors have the same scale range for single y-axis optimization
          const allSameScale = scaleGroups.length === 1;

          if (allSameScale) {
            // Create single y-axis when all sensors share the same scale range
            const scaleGroup = scaleGroups[0];
            
            const yAxis = chart.yAxes.push(
              am5xy.ValueAxis.new(root, {
                extraTooltipPrecision: 1,
                renderer: am5xy.AxisRendererY.new(root, {
                  opposite: false, // Single axis on the left
                }),
                treatZeroAs: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? undefined : 0, // Don't treat zero specially when custom scaling or relay
                strictMinMax: !!(scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor),
                min: scaleGroup.min,
                max: scaleGroup.max,
                baseValue: scaleGroup.hasRelaySensor ? -0.5 : (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined ? scaleGroup.min : undefined), // Set base value to min
                numberFormat: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? "#.#" : undefined, // Ensure proper number formatting
                extraMax: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 0 : undefined, // Don't add extra space beyond max when custom scaling or relay
                extraMin: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 0 : undefined, // Don't add extra space beyond min when custom scaling or relay
                maxPrecision: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 1 : undefined, // Control decimal precision
              }),
            );
            
            // Force exact bounds if custom scaling is applied or relay sensor
            if (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined && scaleGroup.max !== undefined) {
              yAxis.set("min", scaleGroup.min);
              yAxis.set("max", scaleGroup.max);
              yAxis.set("strictMinMax", false);
            } else if (scaleGroup.hasRelaySensor) {
              yAxis.set("min", -0.5);
              yAxis.set("max", 1.5);
              yAxis.set("strictMinMax", true);
            }
            
            yAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));
            
            // Create combined label from all units in the group, but exclude sensor names (empty units)
            const nonEmptyUnits = scaleGroup.units.filter(unit => {
              const isSensorName = visibleSensorsWithData.some(sensor => sensor.sensorName === unit && (!sensor.unit || sensor.unit.trim() === ''));
              return !isSensorName;
            });
            
            // Special labeling for relay sensors
            let displayText: string;
            if (scaleGroup.hasRelaySensor) {
              displayText = 'Binary/Relay'; // Clear label for relay sensors
            } else {
              displayText = nonEmptyUnits.length > 0 ? nonEmptyUnits.join(' / ') : '';
            }
            
            const yAxisLabel = am5.Label.new(root, {
              text: displayText,
              fill: am5.color('#fff'),
              rotation: 0,
              centerX: am5.p50,
              centerY: am5.p50,
              x: -10,
              y: -30,
              fontSize: 14,
              fontWeight: 'bold',
            });
            yAxis.children.push(yAxisLabel);
            
            // Add all units from the group to yAxisArray so sensors can find their axis
            scaleGroup.units.forEach(unit => {
              yAxisArray.push({ [unit.trim()]: yAxis as am5xy.ValueAxis<am5xy.AxisRendererY> });
            });
          } else {
            // Create one y-axis per unique scale range (not per unit)
            scaleGroups.forEach((scaleGroup, index) => {
              
              const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                  extraTooltipPrecision: 1,
                  renderer: am5xy.AxisRendererY.new(root, {
                    opposite: index % 2 === 0 ? false : true,
                  }),
                  treatZeroAs: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? undefined : 0, // Don't treat zero specially when custom scaling or relay
                  strictMinMax: !!(scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor), // Use strict bounds if custom scaling is applied or relay
                  min: scaleGroup.min,
                  max: scaleGroup.max,
                  baseValue: scaleGroup.hasRelaySensor ? -0.5 : (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined ? scaleGroup.min : undefined), // Set base value to min
                  numberFormat: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? "#.#" : undefined, // Ensure proper number formatting
                  extraMax: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 0 : undefined, // Don't add extra space beyond max when custom scaling or relay
                  extraMin: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 0 : undefined, // Don't add extra space beyond min when custom scaling or relay
                  maxPrecision: (scaleGroup.hasCustomScaling || scaleGroup.hasRelaySensor) ? 1 : undefined, // Control decimal precision
                }),
              );
              
              // Force exact bounds if custom scaling is applied or relay sensor
              if (scaleGroup.hasCustomScaling && scaleGroup.min !== undefined && scaleGroup.max !== undefined) {
                yAxis.set("min", scaleGroup.min);
                yAxis.set("max", scaleGroup.max);
                yAxis.set("strictMinMax", false);
              } else if (scaleGroup.hasRelaySensor) {
                yAxis.set("min", -0.5);
                yAxis.set("max", 1.5);
                yAxis.set("strictMinMax", true);
              }
              
              yAxis.get('renderer').labels.template.set('fill', am5.color('#fff'));
              
              // Create combined label from all units in the group, but exclude sensor names (empty units)
              const nonEmptyUnits = scaleGroup.units.filter(unit => {
                const isSensorName = visibleSensorsWithData.some(sensor => sensor.sensorName === unit && (!sensor.unit || sensor.unit.trim() === ''));
                return !isSensorName;
              });
              
              // Special labeling for relay sensors
              let displayText: string;
              if (scaleGroup.hasRelaySensor) {
                displayText = 'Binary/Relay'; // Clear label for relay sensors
              } else {
                displayText = nonEmptyUnits.length > 0 ? nonEmptyUnits.join(' / ') : '';
              }
              
              const yAxisLabel = am5.Label.new(root, {
                text: displayText,
                fill: am5.color('#fff'),
                rotation: 0,
                centerX: am5.p50,
                centerY: am5.p50,
                x: index % 2 === 0 ? -10 : 15,
                y: -30,
                fontSize: 12,
                fontWeight: 'bold',
              });
              yAxis.children.push(yAxisLabel);
              
              // Add all units from the group to yAxisArray so sensors can find their axis
              scaleGroup.units.forEach(unit => {
                yAxisArray.push({ [unit.trim()]: yAxis as am5xy.ValueAxis<am5xy.AxisRendererY> });
              });
            });
          }
        }

        // Create the cursor
        const cursor = chart.set(
          'cursor',
          am5xy.XYCursor.new(root, {
            behavior: 'zoomXY' // Allows drawing a rectangle to zoom into a specific area
          }),
        );

        // Optional: Formatting the selection box
        cursor.selection.setAll({
          fill: am5.color(0x1fc9a7),
          fillOpacity: 0.2,
          stroke: am5.color(0x1fc9a7),
          strokeWidth: 2,
          strokeOpacity: 0.8
        });

        cursor.lineY.set('visible', false);
        cursor.lineX.setAll({
          visible: true,
          stroke: am5.color('#ADD8E6'),
          strokeWidth: 3,
        });

        const tooltip: any = am5.Tooltip.new(root, {
          pointerOrientation: 'right',
          dx: -15,
        });

        tooltip.get('background').setAll({
          fill: am5.color('#001023'),
          stroke: am5.color('#4A5463'),
          opacity: 1,
        });

        chart.plotContainer.set('tooltipPosition', 'pointer');
        chart.plotContainer.set('tooltipHTML', '<div >' + '' + '</div>');
        chart.plotContainer.set('tooltip', tooltip);

        visibleSensorsWithData?.forEach((sensor, index) => {
          const originalUnit = sensor.unit?.trim() || '';
          // Use sensor name for sensors without units (consistent with y-axis creation)
          const normalizedUnit = originalUnit === '' ? sensor.sensorName : originalUnit;
          const matchedArray = yAxisArray.find(obj => normalizedUnit in obj);
          const yAxis = matchedArray ? matchedArray[normalizedUnit] : null;
          if (!yAxis) {
            return;
          }

          // All sensors in sensorsWithData have data, so no need to check again
          // Include unit in the series name for the legend
          const sensorNameWithUnit = sensor.unit ? `${sensor.sensorName} (${sensor.unit})` : sensor.sensorName;
          createSeries(
            chart,
            root,
            sensorNameWithUnit,
            sensor.color,
            xAxis as am5xy.DateAxis<am5xy.AxisRendererX>,
            yAxis as am5xy.ValueAxis<am5xy.AxisRendererY>,
            sensor.dataPoints,
            cursor,
            index,
            scaleGroups.length === 1, // Pass whether all sensors share the same scale range
            sensor // Pass the complete sensor data for dynamic relay detection
          );
        });

        const legend = chart.children.push(
          am5.Legend.new(root, {
            x: am5.percent(50),
            centerX: am5.percent(50),
            layout: am5.GridLayout.new(root, {
              maxColumns: 6,
              fixedWidthGrid: true,
            }),
            setStateOnChildren: false,
            paddingTop: 25,
            useDefaultMarker: true,
          }),
        );

        legend.itemContainers.template.setAll({
          layout: root.horizontalLayout,
        });

        legend.markers.template.setAll({
          width: 12,
          height: 12,
          centerY: am5.p50,
          y: am5.p50,
        });

        legend.markerRectangles.template.setAll({
          cornerRadiusTL: 10,
          cornerRadiusTR: 10,
          cornerRadiusBL: 10,
          cornerRadiusBR: 10,
          width: 12,
          height: 12,
        });

        legend.labels.template.setAll({
          fill: am5.color(0xffffff),
          centerY: am5.p50,
          y: am5.p50,
        });
        legend.data.setAll(chart.series.values);
        legend.itemContainers.template.states.create('hover', {});

        // Legend click handling will be implemented later with proper AmCharts API
        // For now, focus on unit consolidation and y-axis optimization

        chartInstanceRef.current = chart;
        chartCreationRef.current = false; // Reset the flag after chart creation is complete
        setChartCreated(true); // Mark chart as created
        setLastChartDataKey(chartDataKey); // Store the data key

        return () => {
          root.dispose();
        };
      }, 200); // 200ms debounce delay for chart creation
    }
  }, [selectedSensors, hasNoData, initialDataLoadComplete, customScaling, chartMode]); 
  useEffect(() => {
  if (triggerInitialLoad) {
    // default last 1 hour logic
    applyLastOneHour();
  }
}, [triggerInitialLoad]);
  return (
    <div className="analysis-tab-container">
      <div className="analysis-header">
        <div className="analysis-title-section">
          <h2>Analysis</h2>
        </div>
        <div className="analysis-controls">
          {/* Chart mode toggle buttons */}
          <div className="chart-mode-toggle">
            <button
              className={`chart-mode-btn ${chartMode === 'individual' ? 'active' : ''}`}
              onClick={() => handleChartModeChange('individual')}
            >
              Individual
            </button>
            <button
              className={`chart-mode-btn ${chartMode === 'group' ? 'active' : ''}`}
              onClick={() => handleChartModeChange('group')}
            >
              Group
            </button>
          </div>
          <div className="calendar-button-container" style={{ position: 'relative', zIndex: '7' }}>
            <div
              id='calendar-btn'
              style={{
                border: showCalendarModal
                  ? '1px solid var(--Dark-mode-400, #4A5463)'
                  : '1px solid var(--Dark-mode-400, #4A5463)',
                background: showCalendarModal ? 'var(--Dark-mode-800, #001023)' : 'inherit',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                borderRadius: '6px',
                gap: '8px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCalendarToggle();
              }}
            >
              <img src={calendarIcon} alt='calendarIcon' width="16" height="16" />
              <span style={{ color: '#e0e0e0', fontSize: '14px' }}>
                {selectedTimeRange !== 'Custom date range'
                  ? selectedTimeRange
                  : (appliedStartDate && appliedEndDate
                    ? `${appliedStartDate.toLocaleDateString()} ${appliedStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${appliedEndDate.toLocaleDateString()} ${appliedEndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                    : (startDate && endDate
                      ? `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleDateString()} ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                      : 'Select date range'
                    )
                  )
                }
              </span>
              <img src={downIcon} className={!showCalendarModal ? '' : 'rotate-180'} alt='downIcon' />
            </div>
            {showCalendarModal && (
              <div
                ref={calendarModalRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  zIndex: 1000,
                  background: 'transparent'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DateRangePicker
                  sidebarOptions={sidebarOptions}
                  initialSelectedOption={getSelectedSidebarOption()}
                  onApply={({ startDate: s, endDate: e }) => {
                    setStartDate(s);
                    setEndDate(e);
                    setAppliedStartDate(s);
                    setAppliedEndDate(e);

                    // Check if dates match any predefined time range by comparing with calculated ranges
                    const now = new Date();
                    let matchedTimeRange = null;

                    // Test each predefined range to see if it matches the selected dates
                    const timeRanges = [
                      { label: 'Last 1 hour', hours: 1 },
                      { label: 'Last 2 hours', hours: 2 },
                      { label: 'Last 4 hours', hours: 4 },
                      { label: 'Last 8 hours', hours: 8 },
                      { label: 'Last 24 hours', hours: 24 },
                      { label: 'Last 7 days', hours: 7 * 24 }
                    ];

                      for (const range of timeRanges) {
                      const expectedStart = new Date(now.getTime() - range.hours * 60 * 60 * 1000);
                      // Account for the 10-minute buffer added by DateRangePicker (commented out)
                      // expectedStart.setMinutes(expectedStart.getMinutes() - 10);
                      const expectedEnd = new Date(now);
                      // expectedEnd.setMinutes(expectedEnd.getMinutes() + 10);
                      
                      const timeDiffStart = Math.abs(s.getTime() - expectedStart.getTime());
                      const timeDiffEnd = Math.abs(e.getTime() - expectedEnd.getTime());

                      // Allow 5-minute tolerance for matching predefined ranges (no buffer, just variance)
                      if (timeDiffStart < 5 * 60 * 1000 && timeDiffEnd < 5 * 60 * 1000) {
                        matchedTimeRange = range.label;
                        break;
                      }
                    }

                    // Set the appropriate time range
                    if (matchedTimeRange) {
                      setSelectedTimeRange(matchedTimeRange);
                    } else {
                      setSelectedTimeRange('Custom date range');
                    }

                    setShowDatePicker(false);
                    setShowCalendarModal(false);
                    handleDateRangeApply();
                  }}
                  onCancel={() => {
                    // Restore previous applied dates instead of clearing
                    setStartDate(appliedStartDate);
                    setEndDate(appliedEndDate);
                    setShowDatePicker(false);
                    setShowCalendarModal(false);
                  }}
                  initialStartDate={startDate ?? appliedStartDate ?? undefined}
                  initialEndDate={endDate ?? appliedEndDate ?? undefined}
                  showTimeInputs={true}
                />
              </div>
            )}
          </div>
          {/* Export Button */}
          <div className="export-button-container">
            <button
              className={`export-btn ${
                selectedSensors.length > 0 && !isExporting 
                  ? 'export-btn--enabled' 
                  : 'export-btn--disabled'
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (selectedSensors.length === 0 || isExporting) {
                  return;
                }
                handleExportData();
              }}
              disabled={selectedSensors.length === 0 || isExporting}
            >
              <img src={downloadIcon} alt='downloadIcon' width="16" height="16" />
              <span>{isExporting ? 'Downloading...' : `Download`}</span>
            </button>
          </div>
          <div className='history-container-filter'>
            <div style={{ position: 'relative', zIndex: '8' }}>
              <div
                id='filter-btn'
                style={{
                  border: showFilterPopup
                    ? '1px solid var(--Dark-mode-400, #4A5463)'
                    : '1px solid var(--Dark-mode-400, #4A5463)',
                  background: showFilterPopup ? 'var(--Dark-mode-800, #001023)' : 'inherit',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterDropdownClick();
                }}
              >
                <img src={filterIcon} alt='filterIcon' />
                Sensors {`(${selectedSensors?.length})`}
                <img src={downIcon} className={!showFilterPopup ? '' : 'rotate-180'} alt='downIcon' />
              </div>

              {showFilterPopup && (
                <div className='trends-filter-modal-container' ref={filterRef}>
                  <div className='trends-filter-modal-header'>Add/Remove sensors</div>
                  <div className='trends-filter-search history-search relative'>
                    <img src={searchIcon} alt='search' className='history-search-icon' />
                    <input
                      type='text'
                      value={searchInputVal}
                      onChange={handleSearch}
                      className='history-filter-search'
                      placeholder="Search sensors..."
                    />
                  </div>
                  <div className='trends-filter-modal-body'>
                    {loading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#bfc9db' }}>
                        Loading sensors...
                      </div>
                    ) : (
                      // Replace the sensor checkbox item in the filter modal (around line 700-730)
                      searchFiltered?.map((sensor) => {
                        const hasData = sensor.dataPoints && sensor.dataPoints.length > 0;
                        const isSelected = pendingSensors.includes(sensor.channelId);
                        const shouldDisable = !isSelected && chartMode === 'group' && pendingSensors.length >= 3;

                        return (
                          <div key={sensor.id} className='checkbox-main-container'>
                            <label className="sensor-checkbox-item">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSensorChange(sensor.channelId)}
                                disabled={shouldDisable}
                              />
                              <span className="sensor-checkbox-label">
                                {/* Updated color indicator with click handler */}
                                <span
                                  className="sensor-color-indicator"
                                  style={{
                                    backgroundColor: sensor.color,
                                    cursor: 'pointer',
                                    position: 'relative'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent form submission
                                    e.stopPropagation(); // Prevent checkbox change
                                    openColorPicker(sensor.id, e);
                                  }}
                                  title="Click to change color"
                                >
                                  <img
                                    src={colorPickerIcon}
                                    alt="color picker"
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: '12px',
                                      height: '12px',
                                      opacity: 0.7,
                                      display: isSelected ? 'block' : 'none'
                                    }}
                                  />
                                </span>
                                <span className="sensor-info">
                                  <span className="sensor-name">{sensor.sensorName}</span>
                                  {hasData && (
                                    <span className="sensor-unit">({sensor.unit})</span>
                                  )}
                                </span>
                              </span>
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className='trends-filter-modal-footer'>
                    <button className='footer-btn' onClick={handleClearButton}>
                      Clear chart
                    </button>
                    <button className='footer-btn' onClick={handleApplyButton}>
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showColorPickerFor && colorPickerPosition && (
        <div className='color-picker-container'
          ref={colorPickerRef}

          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            marginBottom: '10px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            Choose color for {sensors.find(s => s.id === showColorPickerFor)?.sensorName}
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Current:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: sensors.find(s => s.id === showColorPickerFor)?.color,
                  border: '2px solid #4A5463',
                  borderRadius: '4px'
                }}
              />
              <div style={{ color: '#fff' }}>
                {sensors.find(s => s.id === showColorPickerFor)?.color.toUpperCase()}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Quick picks:</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '6px',
              marginBottom: '10px'
            }}>
              {colorOptions.map((color) => (
                <button
                  key={color}
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: color,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSensorColorChange(showColorPickerFor, color, true); // true = close picker
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title={color.toUpperCase()}
                />
              ))}
            </div>
          </div>

          <div>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Custom color:</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={sensors.find(s => s.id === showColorPickerFor)?.color || '#FFFFFF'}
                onChange={(e) => {
                  e.stopPropagation();
                  const newColor = e.target.value;
                  // Pass false to keep picker open
                  handleSensorColorChange(showColorPickerFor, newColor, false);
                }}
                style={{
                  width: '100%',
                  height: '40px',
                  border: '1px solid #4A5463',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={sensors.find(s => s.id === showColorPickerFor)?.color || '#FFFFFF'}
                onChange={(e) => {
                  e.stopPropagation();
                  const newColor = e.target.value;
                  // Validate hex color format
                  if (/^#[0-9A-F]{6}$/i.test(newColor)) {
                    handleSensorColorChange(showColorPickerFor, newColor);
                  }
                }}
                onBlur={(e) => {
                  const newColor = e.target.value;
                  // If not starting with #, add it
                  if (!newColor.startsWith('#') && newColor.length > 0) {
                    handleSensorColorChange(showColorPickerFor, '#' + newColor);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: '#0a1a29',
                  border: '1px solid #4A5463',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px'
                }}
                placeholder="#FFFFFF or hex code"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{
                marginTop: '12px',
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: '1px solid #4A5463',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                width: '100%',
                fontSize: '12px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPickerFor(null);
                setColorPickerPosition(null);
              }}
            >
              Close
            </button>
            <button
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#0078D4',
                border: '1px solid #0078D4',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Apply the color (if using temporary state)
                setLastChartDataKey('');
                setShowColorPickerFor(null);
                setColorPickerPosition(null);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
      <div className="analysis-chart-container" style={{ width: "100%", minWidth: "800px", position: "relative" }}>
        
        {/* Custom Scaling Button - show only for group mode */}
        {chartMode === 'group' && (
          <button
            className="custom-scaling-button"
            onClick={handleCustomScalingOpen}
              style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                zIndex: showFilterPopup ? 5 : 15, 
                background: 'white',
                color: '#333333',
                border: '1px solid #cccccc',
                borderRadius: '6px',
                padding: '3px 3px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: showFilterPopup ? 0.5 : 1 
              }}
              onMouseEnter={(e) => {
                if (!showFilterPopup) {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#999999';
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilterPopup) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#cccccc';
                }
              }}
              title="Custom scaling"
            >
              <img src={scaleIcon} alt="Custom scaling" style={{width: '16px', height: '16px'}} />
            </button>
        )}
        
        {/* Render charts based on mode */}
        {chartMode === 'individual' ? (
          <IndividualChart
            sensors={sensors}
            selectedSensors={selectedSensors}
            selectedTimeRange={selectedTimeRange}
            customScaling={customScaling}
            uomRanges={uomRanges}
            hasNoData={hasNoData}
            initialDataLoadComplete={initialDataLoadComplete}
            chartDataLoading={chartDataLoading}
            onCustomScalingOpen={handleCustomScalingOpen}
          />
        ) : (
          // Group chart (existing functionality)
          <>
            {!sensorsInitialized ? (
              <div className="loading-message">
                <Loader />
                <p>Initializing sensors...</p>
              </div>
            ) : selectedSensors.length > 0 || chartCreated || hasValidChartData || chartDataSnapshot.length > 0 ? (
              <div style={{ position: 'relative', width: "100%" }}>
                {chartDataLoading && !chartCreated && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(10, 25, 41, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: '8px',
                    gap: '12px'
                  }}>
                    <Loader />
                  </div>
                )}
                {initialDataLoadComplete && hasNoData && !chartDataLoading && !chartCreated && !hasValidChartData && chartDataSnapshot.length === 0 ? (
                  <div className="no-data-message">
                    <p>No data found for the selected sensors in the {selectedTimeRange.toLowerCase()} time period.</p>
                    <p style={{ fontSize: '14px', color: '#8b9cb8', marginTop: '8px' }}>
                      Try selecting a different time range or different sensors.
                    </p>
                  </div>
                ) : (
                  <div
                    id="analysis-chartdiv"
                    style={{
                      width: "100%",
                      minWidth: "800px",
                      height: "500px"
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="no-data-message">
                <p>Please select at least one sensor to display the chart.</p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Custom Scaling Modal */}
      {showCustomScalingModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCustomScalingClose();
            }
          }}
        >
          <div 
            className="custom-scaling-modal"
            style={{
              background: '#0a1929',
              border: '1px solid #4a5463',
              borderRadius: '8px',
              width: '270px',
              color: '#e0e0e0',
              marginTop: '50px',
              paddingLeft: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '8px',
              paddingBottom: '10px',
            }}>
              <h3 style={{
                color: '#fff',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0',
                flexGrow: 1,
                textAlign: 'center'
              }}>
                Custom Scaling
              </h3>
              <button
                onClick={handleCustomScalingClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#e0e0e0',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#22314a';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#e0e0e0';
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div 
              style={{ 
                marginBottom: '24px', 
                maxHeight: '400px', 
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#4a5463 #162032'
              }}
              ref={(el) => {
                if (el) {
                  const style = document.createElement('style');
                  style.textContent = `
                    .custom-scaling-scroll::-webkit-scrollbar {
                      width: 6px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-track {
                      background: #162032;
                      border-radius: 3px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-thumb {
                      background: #4a5463;
                      border-radius: 3px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-thumb:hover {
                      background: #5a6573;
                    }
                  `;
                  if (!document.head.querySelector('.custom-scaling-scrollbar-style')) {
                    style.className = 'custom-scaling-scrollbar-style';
                    document.head.appendChild(style);
                  }
                  el.className = 'custom-scaling-scroll';
                }
              }}
            >
              {Object.entries(unitScalingValues).map(([unit, values], index, array) => (
                <div key={unit}>
                  {/* Axis Field */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{
                      color: '#bfc9db',
                      fontSize: '14px'
                    }}>
                      Axis : {values.displayName || unit}
                    </div>
                  </div>
                  
                  {/* Min and Max Fields in a row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0px',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#bfc9db',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}>
                        Min
                      </label>
                      <input
                        type="number"
                        value={values.min}
                        onChange={(e) => handleUnitValueChange(unit, 'min', e.target.value)}
                        placeholder="Min value"
                        style={{
                          width: '70%',
                          background: '#22314a',
                          color: '#e0e0e0',
                          border: '1px solid #4a5463',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#bfc9db',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}>
                        Max
                      </label>
                      <input
                        type="number"
                        value={values.max}
                        onChange={(e) => handleUnitValueChange(unit, 'max', e.target.value)}
                        placeholder="Max value"
                        style={{
                          width: '70%',
                          background: '#22314a',
                          color: '#e0e0e0',
                          border: '1px solid #4a5463',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Horizontal separator line - only show if not the last item */}
                  {index < array.length - 1 && (
                    <div style={{
                      width: '100%',
                      height: '1px',
                      background: '#4a5463',
                      margin: '16px 0'
                    }} />
                  )}
                </div>
              ))}
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '-20px',
              paddingBottom: '10px'
            }}>
                <button
                  onClick={handleCustomScalingClose}
                  style={{
                    background: 'transparent',
                    color: '#e0e0e0',
                    border: '1px solid #4a5463',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22314a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomScalingApply}
                  disabled={!isApplyButtonEnabled()}
                  style={{
                    background: isApplyButtonEnabled() ? '#1ec9a7' : '#4a5463',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: isApplyButtonEnabled() ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    marginRight: '14px'
                  }}
                  onMouseEnter={(e) => {
                    if (isApplyButtonEnabled()) {
                      e.currentTarget.style.background = '#17b496';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isApplyButtonEnabled()) {
                      e.currentTarget.style.background = '#1ec9a7';
                    }
                  }}
                >
                  Apply
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisTab;